import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '@/lib/customSupabaseClient';
import {
    Loader2, ArrowLeft, Video, Info, ListChecks, Target, HeartPulse, Shield, BarChart3,
    TrendingUp, TrendingDown, BookText, Code, Repeat, Weight, Timer, Zap, Clock, ChevronsRight, Dumbbell,
    Ruler, Activity, AlertTriangle, CheckCircle, BrainCircuit, Shuffle, Hand, SlidersHorizontal, BookOpen, UserCheck, Microscope, Circle, MessageSquare, Star,
    Mic, RefreshCw, Plus, Package, CalendarDays, Bookmark, BookmarkCheck, ExternalLink, Mail,
    Volume2, Square, Play, Pause, ChevronDown, ThumbsUp, ThumbsDown, CircleDot, Copy, Check, UserPlus,
} from 'lucide-react';
import { useExerciseBookmarks } from '@/hooks/useExerciseBookmarks';
import BookmarkForAthleteDialog from '@/components/exercises/BookmarkForAthleteDialog';
import { useAuth } from '@/contexts/SupabaseAuthContext';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { VimeoEmbed } from '@/components/exercises/VimeoEmbed';
import { VideoSettingsPanel } from '@/components/exercises/VideoSettingsPanel';
import { VideoUploadPanel } from '@/components/exercises/VideoUploadPanel';

// Keep in sync with supabase/functions/_shared/vimeo-projects.ts.
const VIMEO_PROJECTS = [
  { value: 'myphysio', label: 'MyPhysio' },
  { value: 'shoulder', label: 'Shoulder' },
  { value: 'knee', label: 'Knee' },
  { value: 'exercise_db', label: 'Exercise DB' },
  { value: 'spinal', label: 'Spinal' },
  { value: 'adductor', label: 'Adductor' },
  { value: 'elbow', label: 'Elbow' },
  { value: 'neck', label: 'Neck' },
  { value: 'squat_companion', label: 'Squat Companion' },
  { value: 'kettlebell', label: 'Kettlebell' },
  { value: 'mobility', label: 'Mobility' },
  { value: 'mobility_flows', label: 'Mobility Flows' },
  { value: 'longevity_blueprint', label: 'Longevity Blueprint' },
  { value: 'stability_training', label: 'Stability Training' },
  { value: 'headstrong', label: 'HeadStrong' },
  { value: 'shoulder_instability', label: 'Shoulder Instability' },
  { value: 'runners', label: 'Runners' },
  { value: 'health_hub', label: 'Health Hub' },
  { value: 'workout_tracker', label: 'Workout Tracker' },
  { value: 'felix', label: 'Felix' },
  { value: 'birken', label: 'Birken' },
  { value: 'parallel', label: 'Parallel' },
  { value: 'knee_notion', label: 'Knee Notion' },
  { value: 'workout', label: 'Workout' },
];


const PageSkeleton = () => (
  <div className="p-4 md:p-8">
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-10 rounded-md" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-7 w-72" />
          <Skeleton className="h-4 w-48" />
        </div>
      </div>
      <Card>
        <CardHeader><Skeleton className="h-5 w-56" /></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
      <Skeleton className="aspect-video w-full rounded-lg" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Skeleton className="h-40 rounded-lg" />
          <Skeleton className="h-40 rounded-lg" />
          <Skeleton className="h-40 rounded-lg" />
        </div>
        <div className="space-y-6">
          <Skeleton className="h-48 rounded-lg" />
          <Skeleton className="h-32 rounded-lg" />
          <Skeleton className="h-32 rounded-lg" />
        </div>
      </div>
    </div>
  </div>
);

const DataPoint = ({ icon: Icon, label, value, unit = '', children }) => {
    if (value === null || value === undefined || value === '') return null;
    return (
        <div className="flex items-start gap-3">
            <Icon className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-1" />
            <div>
                <p className="text-sm text-muted-foreground">{label}</p>
                {children ? <div className="font-semibold text-foreground">{children}</div> : <p className="font-semibold text-foreground">{value} {unit}</p>}
            </div>
        </div>
    );
};

const BooleanDataPoint = ({ icon: Icon, label, value }) => {
  if (value === null || value === undefined) return null;
  return (
      <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          <p className="text-sm text-muted-foreground">{label}</p>
          {value ? <CheckCircle className="h-5 w-5 text-green-500" /> : <AlertTriangle className="h-5 w-5 text-amber-500" />}
          <p className="font-semibold text-foreground">{value ? 'Yes' : 'No'}</p>
      </div>
  );
};

const ListDataPoint = ({ icon: Icon, label, value }) => {
    if (!value || value.length === 0) return null;
    return (
        <DataPoint icon={Icon} label={label}>
            <div className="flex flex-wrap gap-2">
                {value.map((item, index) => <Badge key={index} variant="outline">{item}</Badge>)}
            </div>
        </DataPoint>
    );
};

const TempoDisplay = ({ eccentric, pause, concentric, rest }) => {
    if ([eccentric, pause, concentric, rest].every(v => v === null || v === undefined)) return null;
    return (
        <DataPoint icon={Clock} label="Tempo">
            <div className="flex items-center gap-1 font-mono text-lg">
                <Badge variant="secondary" className="px-3 py-1">{eccentric || 'X'}</Badge>
                <ChevronsRight className="h-4 w-4 text-muted-foreground" />
                <Badge variant="secondary" className="px-3 py-1">{pause || 'X'}</Badge>
                <ChevronsRight className="h-4 w-4 text-muted-foreground" />
                <Badge variant="secondary" className="px-3 py-1">{concentric || 'X'}</Badge>
                <ChevronsRight className="h-4 w-4 text-muted-foreground" />
                <Badge variant="secondary" className="px-3 py-1">{rest || 'X'}</Badge>
            </div>
        </DataPoint>
    );
};

const RPEGauge = ({ value }) => {
    if (value === null || value === undefined) return null;
    return (
        <DataPoint icon={Zap} label="Target RPE">
            <div className="w-full flex items-center gap-4">
                <Progress value={value * 10} className="h-3 flex-grow" />
                <span className="font-bold text-xl">{value}/10</span>
            </div>
        </DataPoint>
    );
};

const RangeSlider = ({ min, max, label, icon }) => {
    if (min === null || max === null || min === undefined || max === undefined) return null;
    return (
        <DataPoint icon={icon} label={label}>
            <div className="flex items-center gap-2">
                <Badge>{min}</Badge>
                <span>-</span>
                <Badge>{max}</Badge>
            </div>
        </DataPoint>
    );
};

const SessionProtocolCard = ({ protocol }) => {
    const tabs = [
        { name: "Core", icon: Dumbbell },
        { name: "Load & Intensity", icon: Weight },
        { name: "Execution", icon: Activity },
        { name: "Clinical", icon: Microscope }
    ];

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-xl">{protocol.sessions?.name || 'Unknown Session'}</CardTitle>
                <CardDescription>{protocol.sessions?.programs?.name ? `In Program: ${protocol.sessions.programs.name}` : 'Standalone session'}</CardDescription>
            </CardHeader>
            <CardContent>
                <Tabs defaultValue="Core" className="w-full">
                    <TabsList className="grid w-full grid-cols-4">
                        {tabs.map(tab => (
                            <TabsTrigger key={tab.name} value={tab.name}>
                                <tab.icon className="mr-2 h-4 w-4" />{tab.name}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    <TabsContent value="Core" className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                        <DataPoint icon={Repeat} label="Sets" value={protocol.sets} />
                        <DataPoint icon={Repeat} label="Reps" value={protocol.reps} />
                        <RangeSlider icon={Repeat} label="Rep Range" min={protocol.reps_range_min} max={protocol.reps_range_max} />
                        <DataPoint icon={Timer} label="Duration" value={protocol.duration_seconds} unit="s" />
                        <DataPoint icon={Ruler} label="Distance" value={protocol.distance_meters} unit="m" />
                        <DataPoint icon={Timer} label="Rest (Sets)" value={protocol.rest_between_sets_seconds} unit="s" />
                        <DataPoint icon={Timer} label="Rest (Exercises)" value={protocol.rest_between_exercises_seconds} unit="s" />
                        <DataPoint icon={BookOpen} label="Group" value={protocol.exercise_group} />
                    </TabsContent>
                    <TabsContent value="Load & Intensity" className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                        <DataPoint icon={Dumbbell} label="Load Type" value={protocol.load_type} />
                        <DataPoint icon={Weight} label="Weight" value={protocol.weight_kg} unit="kg" />
                        <DataPoint icon={BarChart3} label="Percentage 1RM" value={protocol.percentage_1rm} unit="%" />
                        <RPEGauge value={protocol.target_rpe} />
                        <DataPoint icon={Activity} label="Stability Challenge" value={protocol.stability_challenge} />
                        <DataPoint icon={SlidersHorizontal} label="Assistance Level" value={protocol.assistance_level} />
                    </TabsContent>
                    <TabsContent value="Execution" className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                        <TempoDisplay eccentric={protocol.tempo_eccentric} pause={protocol.tempo_pause} concentric={protocol.tempo_concentric} rest={protocol.tempo_rest} />
                        <DataPoint icon={BarChart3} label="Range of Motion" value={protocol.range_of_motion_percentage} unit="%" />
                        <DataPoint icon={Target} label="Technique Focus" value={protocol.technique_focus} />
                        <ListDataPoint icon={ListChecks} label="Form Cues" value={protocol.form_cues} />
                        <DataPoint icon={BrainCircuit} label="Neurological Focus" value={protocol.neurological_focus} />
                        <DataPoint icon={Shuffle} label="Bilateral/Unilateral" value={protocol.bilateral_vs_unilateral} />
                        <DataPoint icon={Hand} label="Open/Closed Chain" value={protocol.open_vs_closed_chain} />
                        <DataPoint icon={Dumbbell} label="External Load Type" value={protocol.external_load_type} />
                    </TabsContent>
                    <TabsContent value="Clinical" className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                        <DataPoint icon={Microscope} label="Therapeutic Intent" value={protocol.therapeutic_intent} />
                        <DataPoint icon={Target} label="Tissue Target" value={protocol.tissue_target} />
                        <DataPoint icon={BookText} label="Clinical Rationale" value={protocol.clinical_rationale} />
                        <DataPoint icon={TrendingUp} label="Progression Criteria" value={protocol.progression_criteria} />
                        <DataPoint icon={TrendingDown} label="Regression Criteria" value={protocol.regression_criteria} />
                        <DataPoint icon={AlertTriangle} label="Contraindications" value={protocol.contraindications} />
                        <DataPoint icon={AlertTriangle} label="Stop Criteria" value={protocol.stop_criteria} />
                        <BooleanDataPoint icon={UserCheck} label="Pain Monitoring" value={protocol.pain_monitoring} />
                        <BooleanDataPoint icon={UserCheck} label="Fatigue Monitoring" value={protocol.fatigue_monitoring} />
                        <BooleanDataPoint icon={UserCheck} label="Is Assessment" value={protocol.is_assessment} />
                        <BooleanDataPoint icon={UserCheck} label="Is Optional" value={protocol.is_optional} />
                    </TabsContent>
                </Tabs>
            </CardContent>
        </Card>
    )
};

function ExerciseDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { can } = useAuth();
    const canEdit = can('exercises.edit');
    const canSeeEngagement = can('exercises.engagement');
    const canEditThumbnail = can('exercises.thumbnail');
    const canEditVideoPrivacy = can('exercises.videoPrivacy');
    const canUploadVideo = can('exercises.videoUpload');
    const videoPlayerRef = useRef(null);
    const [exercise, setExercise] = useState(null);
    const [sessionProtocols, setSessionProtocols] = useState([]);
    const [muscles, setMuscles] = useState(null);
    const [userEngagement, setUserEngagement] = useState(null);
    const [evidence, setEvidence] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fetchingEvidence, setFetchingEvidence] = useState(false);
    const [reviewingEvidence, setReviewingEvidence] = useState({});
    const [generatingVoiceover, setGeneratingVoiceover] = useState(false);
    const [voiceoverLanguage, setVoiceoverLanguage] = useState('en');
    const [voiceoversByLanguage, setVoiceoversByLanguage] = useState({});
    const [syncingVimeo, setSyncingVimeo] = useState(false);
    const [vimeoProject, setVimeoProject] = useState('');
    const [userProfiles, setUserProfiles] = useState({});
    const voiceover = useMemo(
        () => voiceoversByLanguage[voiceoverLanguage] ?? null,
        [voiceoversByLanguage, voiceoverLanguage]
    );
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackProgress, setPlaybackProgress] = useState(0);
    const audioRef = useRef(null);
    const progressIntervalRef = useRef(null);
    const { isBookmarked, toggleBookmark, collections } = useExerciseBookmarks();
    const [bookmarkForAthleteOpen, setBookmarkForAthleteOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [extractingFrames, setExtractingFrames] = useState(false);
    const [frameExtraction, setFrameExtraction] = useState(null);

    const fetchExerciseData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const { data: detailData, error: detailError } = await supabase
                .rpc('get_exercise_detail_enhanced', { p_exercise_id: id });
            
            if (detailError) throw detailError;

            if (detailData) {
                setExercise({
                    ...(detailData.exercise || {}),
                    equipment: detailData.equipment || [],
                    phases: detailData.phases || [],
                    usage_stats: detailData.usage_stats || {},
                    phase_distribution: detailData.phase_distribution || {},
                    usage_timeline: detailData.usage_timeline || [],
                    recent_usage: detailData.recent_usage || [],
                    has_voiceover: detailData.has_voiceover || false,
                    mesh_keys: detailData.mesh_keys || []
                });
            } else {
                setExercise(null);
            }
        } catch (err) {
            setError('Failed to fetch exercise data. Please try again.');
            console.error('Exercise detail fetch error:', err);
        } finally {
            setLoading(false);
        }

        try {
            const [protocolsRes, musclesRes, engagementRes, evidenceRes, voiceoverRes] = await Promise.allSettled([
                supabase
                    .from('session_exercises')
                    .select(`*, sessions ( name, programs ( name ) )`)
                    .eq('exercise_id', id),
                supabase.rpc('get_exercise_muscles_detailed', { p_exercise_id: id }),
                canSeeEngagement
                    ? supabase.rpc('get_exercise_user_engagement', { p_exercise_id: id })
                    : Promise.resolve({ data: null, error: null }),
                supabase
                    .from('exercise_evidence')
                    .select(`evidence_id, review_status, relevance_score, evidence_sources ( id, title, authors, journal, publication_year, abstract, doi, pubmed_id, study_type, relevance_score, url )`)
                    .eq('exercise_id', id),
                supabase
                    .from('exercise_voice_overs')
                    .select(`voice_over_id, is_active, voice_overs ( audio_file_url, text_content, audio_duration_seconds, language_code, file_size_bytes )`)
                    .eq('exercise_id', id)
                    .eq('is_active', true)
                    .order('created_at', { ascending: false }),
            ]);

            if (protocolsRes.status === 'fulfilled' && !protocolsRes.value.error) {
                setSessionProtocols(protocolsRes.value.data || []);
            }
            if (musclesRes.status === 'fulfilled' && !musclesRes.value.error) {
                setMuscles(musclesRes.value.data || null);
            }
            if (engagementRes.status === 'fulfilled' && !engagementRes.value.error) {
                const engagement = engagementRes.value.data || null;
                setUserEngagement(engagement);

                const userIds = [...new Set(
                    (engagement?.recent_feedback || [])
                        .map(fb => fb.user_id)
                        .filter(Boolean)
                )];
                if (userIds.length > 0) {
                    const { data: profiles } = await supabase
                        .rpc('staff_get_user_profiles', { p_user_ids: userIds });
                    if (profiles) {
                        const profileMap = {};
                        profiles.forEach(p => { profileMap[p.id] = p; });
                        setUserProfiles(profileMap);
                    }
                }
            }
            if (evidenceRes.status === 'fulfilled' && !evidenceRes.value.error) {
                setEvidence(evidenceRes.value.data || []);
            }
            if (voiceoverRes.status === 'fulfilled' && !voiceoverRes.value.error) {
                const rows = voiceoverRes.value.data || [];
                const byLang = {};
                for (const row of rows) {
                    const lang = row.voice_overs?.language_code;
                    if (lang && !byLang[lang]) {
                        byLang[lang] = row.voice_overs;
                    }
                }
                setVoiceoversByLanguage(byLang);
            }
        } catch (err) {
            console.error('Secondary data fetch error:', err);
        }
    };

    useEffect(() => {
        if (id) {
            fetchExerciseData();
        }
    }, [id]);
    
    const handleFetchEvidence = async () => {
        setFetchingEvidence(true);
        try {
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
            if (sessionError || !session) {
                toast.error("Authentication Error", { description: "You must be logged in to fetch evidence." });
                return;
            }
    
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetchEvidence?exercise_id=${exercise.id}&min_relevance=3`, {
                headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json'
                }
            });
    
            const data = await response.json();
    
            if (!response.ok || data.error) {
                throw new Error(data.error || 'Something went wrong');
            }
    
            toast.success("Evidence Fetched!", { description: `Found ${data.summary.totalArticlesFound} articles, made ${data.summary.newConnections} new connections.` });
            
            await fetchExerciseData();
    
        } catch (error) {
            toast.error("Failed to Fetch Evidence", { description: error.message || "An unexpected error occurred." });
        } finally {
            setFetchingEvidence(false);
        }
    };

    const handleReviewEvidence = async (evidenceId, status) => {
        setReviewingEvidence(prev => ({ ...prev, [evidenceId]: true }));
        try {
            if (status === 'approved') {
                const { error } = await supabase
                    .from('exercise_evidence')
                    .update({ review_status: 'approved' })
                    .eq('exercise_id', id)
                    .eq('evidence_id', evidenceId);
                if (error) throw error;
                toast.success('Evidence approved — now visible to app users');
                setEvidence(prev => prev.map(item =>
                    item.evidence_id === evidenceId ? { ...item, review_status: 'approved' } : item
                ));
            } else {
                const item = evidence.find(e => e.evidence_id === evidenceId);
                const pubmedId = item?.evidence_sources?.pubmed_id;

                if (pubmedId) {
                    await supabase.from('evidence_blocklist').upsert(
                        { exercise_id: id, pubmed_id: pubmedId, rejected_by: (await supabase.auth.getUser()).data.user?.id },
                        { onConflict: 'exercise_id,pubmed_id' }
                    );
                }

                const { error } = await supabase
                    .from('exercise_evidence')
                    .delete()
                    .eq('exercise_id', id)
                    .eq('evidence_id', evidenceId);
                if (error) throw error;

                await supabase.rpc('cleanup_orphaned_evidence');

                toast.success('Evidence rejected and removed');
                setEvidence(prev => prev.filter(e => e.evidence_id !== evidenceId));
            }
        } catch (err) {
            toast.error('Failed to update evidence', { description: err.message });
        } finally {
            setReviewingEvidence(prev => ({ ...prev, [evidenceId]: false }));
        }
    };

    const handleGenerateVoiceover = async () => {
        const hasCurrentLanguageVoiceover = !!voiceoversByLanguage[voiceoverLanguage];
        if (hasCurrentLanguageVoiceover) {
            const confirmed = window.confirm(
                `This exercise already has a ${voiceoverLanguage.toUpperCase()} voiceover. Regenerating will call the ElevenLabs API again (costs credits). Continue?`
            );
            if (!confirmed) return;
        }
        if (generatingVoiceover) return;
        setGeneratingVoiceover(true);
        try {
            const { data, error } = await supabase.functions.invoke('generate-exercise-voiceovers', {
                body: {
                    exerciseId: id,
                    language: voiceoverLanguage,
                    forceRegenerate: hasCurrentLanguageVoiceover,
                },
            });
            if (error) throw error;
            if (data?.error) {
                throw new Error(data.error);
            }
            if (data?.errorCount > 0) {
                const firstErr = data.errors?.[0];
                toast.error("Voiceover Issue", { description: firstErr?.error || "Generation failed for this exercise." });
            } else {
                toast.success("Voiceover Generated", { description: data?.message || "Voice narration has been created." });
            }
            stopPlayback();
            audioRef.current = null;
            await fetchExerciseData();
        } catch (err) {
            toast.error("Voiceover Failed", { description: err.message });
        } finally {
            setGeneratingVoiceover(false);
        }
    };

    const handleSyncVimeo = async () => {
        if (!vimeoProject) {
            toast.error("Select a Vimeo folder", { description: "Pick the Vimeo project folder to search in." });
            return;
        }
        setSyncingVimeo(true);
        try {
            const { data, error } = await supabase.functions.invoke('vimeo-sync', {
                body: { exerciseIds: [id], projectName: vimeoProject, enableShadowLogic: true },
            });
            if (error) throw error;

            if (data?.errors?.length > 0) {
                const err = data.errors[0];
                toast.error("Vimeo Sync Issue", { description: err.error || "No matching video found." });
            } else if (data?.successCount > 0) {
                const result = data.results?.[0];
                toast.success("Vimeo Synced", {
                    description: result
                        ? `Matched "${result.videoTitle}" from ${result.foundInProject}`
                        : data.message,
                });
            } else {
                toast.info("No Changes", { description: data?.message || "No video data was updated." });
            }
            await fetchExerciseData();
        } catch (err) {
            toast.error("Vimeo Sync Failed", { description: err.message });
        } finally {
            setSyncingVimeo(false);
        }
    };

    const handleExtractFrames = async () => {
        if (extractingFrames) return;
        setExtractingFrames(true);
        try {
            const { data, error } = await supabase.functions.invoke('exercise-frame-extract', {
                body: { exerciseId: id, count: 4 },
            });
            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            setFrameExtraction(data);
            toast.success("Frames extracted", {
                description: `${data?.frames?.length || 0} frames are ready for agent inspection.`,
            });
        } catch (err) {
            toast.error("Frame extraction failed", { description: err.message });
        } finally {
            setExtractingFrames(false);
        }
    };

    const stopPlayback = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }
        setIsPlaying(false);
        setPlaybackProgress(0);
    }, []);

    const togglePlayback = useCallback(() => {
        if (!voiceover?.audio_file_url) return;

        if (isPlaying) {
            stopPlayback();
            return;
        }

        if (!audioRef.current) {
            audioRef.current = new Audio(voiceover.audio_file_url);
            audioRef.current.addEventListener('ended', stopPlayback);
            audioRef.current.addEventListener('error', () => {
                toast.error("Playback Error", { description: "Could not play the voiceover audio." });
                stopPlayback();
            });
        }

        audioRef.current.play().then(() => {
            setIsPlaying(true);
            progressIntervalRef.current = setInterval(() => {
                if (audioRef.current) {
                    const pct = (audioRef.current.currentTime / audioRef.current.duration) * 100;
                    setPlaybackProgress(isFinite(pct) ? pct : 0);
                }
            }, 200);
        }).catch(err => {
            console.error('Audio play error:', err);
            toast.error("Playback Error", { description: "Browser blocked audio playback." });
        });
    }, [voiceover, isPlaying, stopPlayback]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.removeEventListener('ended', stopPlayback);
                audioRef.current = null;
            }
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        };
    }, [stopPlayback]);

    useEffect(() => {
        stopPlayback();
        audioRef.current = null;
    }, [id, stopPlayback]);

    useEffect(() => {
        stopPlayback();
        audioRef.current = null;
    }, [voiceoverLanguage, voiceover?.audio_file_url, stopPlayback]);

  if (loading) return <PageSkeleton />;

  if (error || !exercise) return (
    <div className="p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <Card className="max-w-md mx-auto">
          <CardHeader className="text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-destructive mb-2" />
            <CardTitle>{error || 'Exercise not found.'}</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <Button asChild variant="outline">
              <Link to="/exercises">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Exercises
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{exercise.name} - My Movement Space</title>
        <meta name="description" content={exercise.description} />
      </Helmet>
      <div className="p-4 md:p-8">
        <main className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button asChild variant="outline" size="icon" className="flex-shrink-0">
                <Link to="/exercises"><ArrowLeft className="h-4 w-4" /></Link>
              </Button>
              <div className="flex-grow">
                <h1 className="text-2xl font-semibold tracking-tight">{exercise.name}</h1>
                <div className="flex items-center gap-1.5 mt-1">
                  <p className="text-xs text-muted-foreground font-mono">{exercise.id}</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${exercise.id} — ${exercise.name}`);
                      setCopied(true);
                      toast.success('Copied to clipboard');
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleBookmark(id)}
                className={isBookmarked(id) ? 'text-amber-500 hover:text-amber-600' : 'text-muted-foreground'}
              >
                {isBookmarked(id) ? <BookmarkCheck className="h-4 w-4 mr-1.5" /> : <Bookmark className="h-4 w-4 mr-1.5" />}
                {isBookmarked(id) ? 'Bookmarked' : 'Bookmark'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setBookmarkForAthleteOpen(true)}
                className="text-muted-foreground"
              >
                <UserPlus className="h-4 w-4 mr-1.5" />
                For Athlete
              </Button>
            </div>
          </div>

          {/* Actions Panel */}
          <Card className="mb-6 shadow-none">
            <CardContent className="py-3 px-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground mr-2">Actions</span>
                {canEdit && (
                <>
                <Button variant="outline" size="sm" onClick={handleFetchEvidence} disabled={fetchingEvidence}>
                  {fetchingEvidence ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <BookOpen className="h-3.5 w-3.5 mr-1.5" />}
                  Fetch Evidence
                  {evidence?.length > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">{evidence.length}</Badge>}
                </Button>
                <div className="flex items-center gap-1.5">
                  <Select value={voiceoverLanguage} onValueChange={setVoiceoverLanguage}>
                    <SelectTrigger className="h-8 w-[72px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en" className="text-xs">EN</SelectItem>
                      <SelectItem value="de" className="text-xs">DE</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={handleGenerateVoiceover} disabled={generatingVoiceover}>
                    {generatingVoiceover ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Mic className="h-3.5 w-3.5 mr-1.5" />}
                    Generate Voiceover
                    {voiceoversByLanguage[voiceoverLanguage] && (
                      <Badge variant="success" className="ml-1.5 text-[10px] px-1.5 py-0">Done</Badge>
                    )}
                  </Button>
                </div>
                <div className="flex items-center gap-1.5">
                  <Select value={vimeoProject} onValueChange={setVimeoProject}>
                    <SelectTrigger className="h-8 w-[160px] text-xs">
                      <SelectValue placeholder="Vimeo folder..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {VIMEO_PROJECTS.map((p) => (
                        <SelectItem key={p.value} value={p.value} className="text-xs">
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={handleSyncVimeo} disabled={syncingVimeo || !vimeoProject}>
                    {syncingVimeo ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
                    Sync Vimeo
                    {exercise?.vimeo_link && <Badge variant="success" className="ml-1.5 text-[10px] px-1.5 py-0">Linked</Badge>}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExtractFrames}
                    disabled={extractingFrames || (!exercise?.vimeo_link && !exercise?.vimeo_video_id)}
                  >
                    {extractingFrames ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Video className="h-3.5 w-3.5 mr-1.5" />}
                    Extract Frames
                    {frameExtraction?.frames?.length > 0 && <Badge variant="success" className="ml-1.5 text-[10px] px-1.5 py-0">{frameExtraction.frames.length}</Badge>}
                  </Button>
                </div>
                </>
                )}
                <Button variant="outline" size="sm" onClick={() => navigate(`/session-builder?addExercise=${id}`)}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add to Session
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                  <VimeoEmbed
                    ref={videoPlayerRef}
                    vimeoLink={exercise.vimeo_link}
                    vimeoVideoId={exercise.vimeo_video_id}
                    thumbnailUrl={exercise.video_thumbnail_url}
                    videoDuration={exercise.video_duration}
                  />

                  {canEditThumbnail && (exercise.vimeo_video_id || exercise.vimeo_link) && (
                    <VideoSettingsPanel
                      exerciseId={id}
                      playerRef={videoPlayerRef}
                      frames={frameExtraction?.frames || []}
                      canEditPrivacy={canEditVideoPrivacy}
                      onUpdated={fetchExerciseData}
                    />
                  )}

                  {canUploadVideo && (
                    <VideoUploadPanel
                      exercise={{ ...exercise, id }}
                      folders={VIMEO_PROJECTS}
                      onLinked={fetchExerciseData}
                    />
                  )}

                  {frameExtraction?.frames?.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center text-base">
                          <Video className="h-4 w-4 mr-2 text-muted-foreground" />
                          Extracted Frames
                        </CardTitle>
                        <CardDescription>
                          Agent-readable visual frames only. No exercise fields or Vimeo metadata were updated.
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {frameExtraction.frames.map((frame) => (
                            <a
                              key={`${frame.index}-${frame.timestamp_seconds}`}
                              href={frame.url}
                              target="_blank"
                              rel="noreferrer"
                              className="group block"
                            >
                              <div className="aspect-video overflow-hidden rounded-lg border bg-muted">
                                <img
                                  src={frame.url}
                                  alt={`Extracted frame ${frame.index} at ${frame.timestamp_seconds}s`}
                                  className="h-full w-full object-cover transition-transform group-hover:scale-105"
                                />
                              </div>
                              <p className="mt-1 text-xs text-muted-foreground">
                                Frame {frame.index} · {frame.timestamp_seconds}s
                              </p>
                            </a>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {voiceover && (
                    <Card>
                      <CardContent className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <Button
                            variant={isPlaying ? "default" : "outline"}
                            size="icon"
                            className="h-9 w-9 flex-shrink-0 rounded-full"
                            onClick={togglePlayback}
                          >
                            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                          </Button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Volume2 className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="text-xs font-medium truncate">Voice Narration</span>
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{voiceover.language_code?.toUpperCase() || 'EN'}</Badge>
                            </div>
                            <Progress value={playbackProgress} className="h-1.5" />
                          </div>
                          {isPlaying && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={stopPlayback}>
                              <Square className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center text-base">
                        <Info className="h-4 w-4 mr-2 text-muted-foreground" />
                        Description
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{exercise.description || 'No description provided.'}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center text-base">
                        <ListChecks className="h-4 w-4 mr-2 text-muted-foreground" />
                        Instructions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{exercise.instructions || 'No instructions provided.'}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center text-base">
                        <BookText className="h-4 w-4 mr-2 text-muted-foreground" />
                        Technique & Cues
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{exercise.technique || 'No technique guidance provided.'}</p>
                    </CardContent>
                  </Card>
                  
                  {muscles && muscles.muscles && muscles.muscles.length > 0 && (
                    <Card className="overflow-hidden">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center text-lg">
                            <Activity className="h-5 w-5 mr-3" />
                            Muscle Activation
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            {muscles.muscle_count?.primary > 0 && (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-500 bg-blue-500/10 px-2 py-0.5 rounded-full">
                                {muscles.muscle_count.primary} primary
                              </span>
                            )}
                            {muscles.muscle_count?.secondary > 0 && (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                                {muscles.muscle_count.secondary} secondary
                              </span>
                            )}
                            {muscles.muscle_count?.stabilizer > 0 && (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-500 bg-orange-500/10 px-2 py-0.5 rounded-full">
                                {muscles.muscle_count.stabilizer} stabilizer
                              </span>
                            )}
                          </div>
                        </div>
                        {muscles.muscle_groups && muscles.muscle_groups.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {muscles.muscle_groups.map((group, index) => (
                              <Badge key={index} variant="outline" className="text-[11px] font-normal">{group}</Badge>
                            ))}
                          </div>
                        )}
                      </CardHeader>
                      <CardContent className="p-0">
                        <Accordion type="multiple" defaultValue={['primary']}>
                          {muscles.muscles_by_role?.primary && muscles.muscles_by_role.primary.length > 0 && (
                            <AccordionItem value="primary" className="border-b border-border/50 last:border-b-0">
                              <AccordionTrigger className="px-6 py-3.5 hover:bg-muted/50 transition-colors">
                                <div className="flex items-center gap-3">
                                  <div className="h-2 w-2 rounded-full bg-blue-500" />
                                  <span className="text-sm font-medium">Primary Muscles</span>
                                  <span className="text-xs text-muted-foreground tabular-nums">
                                    {muscles.muscle_count?.primary || 0}
                                  </span>
                                </div>
                                <ChevronDown className="lucide-chevron-down h-4 w-4 text-muted-foreground transition-transform duration-200" />
                              </AccordionTrigger>
                              <AccordionContent className="px-6 pb-4">
                                <div className="space-y-2 pt-1">
                                  {muscles.muscles
                                    .filter(m => m.role === 'primary' || m.role === 'primary_muscle')
                                    .map((muscle, index) => (
                                      <div key={index} className="group flex items-center gap-3 p-3 rounded-lg border border-transparent hover:border-border hover:bg-muted/40 transition-all">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-foreground">{muscle.name}</span>
                                            {muscle.is_deep && (
                                              <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">DEEP</span>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2 mt-1">
                                            {muscle.muscle_group && (
                                              <span className="text-xs text-muted-foreground">{muscle.muscle_group}</span>
                                            )}
                                            {muscle.muscle_group && muscle.body_part && (
                                              <span className="text-muted-foreground/40">·</span>
                                            )}
                                            {muscle.body_part && (
                                              <span className="text-xs text-muted-foreground">{muscle.body_part}</span>
                                            )}
                                          </div>
                                        </div>
                                        {muscle.intensity_level && (
                                          <div className="flex items-center gap-2 flex-shrink-0">
                                            <div className="w-16">
                                              <Progress value={muscle.intensity_level * 10} className="h-1.5" />
                                            </div>
                                            <span className="text-xs font-mono text-muted-foreground w-8 text-right">{muscle.intensity_level}/10</span>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          )}

                          {muscles.muscles_by_role?.secondary && muscles.muscles_by_role.secondary.length > 0 && (
                            <AccordionItem value="secondary" className="border-b border-border/50 last:border-b-0">
                              <AccordionTrigger className="px-6 py-3.5 hover:bg-muted/50 transition-colors">
                                <div className="flex items-center gap-3">
                                  <div className="h-2 w-2 rounded-full bg-emerald-500" />
                                  <span className="text-sm font-medium">Secondary Muscles</span>
                                  <span className="text-xs text-muted-foreground tabular-nums">
                                    {muscles.muscle_count?.secondary || 0}
                                  </span>
                                </div>
                                <ChevronDown className="lucide-chevron-down h-4 w-4 text-muted-foreground transition-transform duration-200" />
                              </AccordionTrigger>
                              <AccordionContent className="px-6 pb-4">
                                <div className="space-y-2 pt-1">
                                  {muscles.muscles
                                    .filter(m => m.role === 'secondary')
                                    .map((muscle, index) => (
                                      <div key={index} className="group flex items-center gap-3 p-3 rounded-lg border border-transparent hover:border-border hover:bg-muted/40 transition-all">
                                        <div className="flex-1 min-w-0">
                                          <span className="text-sm font-medium text-foreground">{muscle.name}</span>
                                          <div className="flex items-center gap-2 mt-1">
                                            {muscle.muscle_group && (
                                              <span className="text-xs text-muted-foreground">{muscle.muscle_group}</span>
                                            )}
                                            {muscle.muscle_group && muscle.body_part && (
                                              <span className="text-muted-foreground/40">·</span>
                                            )}
                                            {muscle.body_part && (
                                              <span className="text-xs text-muted-foreground">{muscle.body_part}</span>
                                            )}
                                          </div>
                                        </div>
                                        {muscle.intensity_level && (
                                          <div className="flex items-center gap-2 flex-shrink-0">
                                            <div className="w-16">
                                              <Progress value={muscle.intensity_level * 10} className="h-1.5" />
                                            </div>
                                            <span className="text-xs font-mono text-muted-foreground w-8 text-right">{muscle.intensity_level}/10</span>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          )}

                          {muscles.muscles_by_role?.stabilizer && muscles.muscles_by_role.stabilizer.length > 0 && (
                            <AccordionItem value="stabilizer" className="border-b-0">
                              <AccordionTrigger className="px-6 py-3.5 hover:bg-muted/50 transition-colors">
                                <div className="flex items-center gap-3">
                                  <div className="h-2 w-2 rounded-full bg-orange-500" />
                                  <span className="text-sm font-medium">Stabilizers</span>
                                  <span className="text-xs text-muted-foreground tabular-nums">
                                    {muscles.muscle_count?.stabilizer || 0}
                                  </span>
                                </div>
                                <ChevronDown className="lucide-chevron-down h-4 w-4 text-muted-foreground transition-transform duration-200" />
                              </AccordionTrigger>
                              <AccordionContent className="px-6 pb-4">
                                <div className="space-y-2 pt-1">
                                  {muscles.muscles
                                    .filter(m => m.role === 'stabilizer')
                                    .map((muscle, index) => (
                                      <div key={index} className="group flex items-center gap-3 p-3 rounded-lg border border-transparent hover:border-border hover:bg-muted/40 transition-all">
                                        <div className="flex-1 min-w-0">
                                          <span className="text-sm font-medium text-foreground">{muscle.name}</span>
                                          {muscle.muscle_group && (
                                            <p className="text-xs text-muted-foreground mt-1">{muscle.muscle_group}</p>
                                          )}
                                        </div>
                                        {muscle.intensity_level && (
                                          <div className="flex items-center gap-2 flex-shrink-0">
                                            <div className="w-16">
                                              <Progress value={muscle.intensity_level * 10} className="h-1.5" />
                                            </div>
                                            <span className="text-xs font-mono text-muted-foreground w-8 text-right">{muscle.intensity_level}/10</span>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                </div>
                              </AccordionContent>
                            </AccordionItem>
                          )}
                        </Accordion>
                      </CardContent>
                    </Card>
                  )}

                  {userEngagement?.recent_feedback && userEngagement.recent_feedback.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center text-lg">
                          <MessageSquare className="h-5 w-5 mr-3" />
                          Recent Athlete Feedback
                        </CardTitle>
                        <CardDescription>
                          Last {userEngagement.recent_feedback.length} comments from athletes
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {userEngagement.recent_feedback.map((feedback, index) => {
                            const profile = userProfiles[feedback.user_id];
                            return (
                            <div key={index} className="p-4 bg-muted rounded-lg border">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <UserCheck className="h-4 w-4 text-muted-foreground" />
                                  {profile ? (
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-foreground">{profile.full_name}</span>
                                      {profile.email && (
                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                          <Mail className="h-3 w-3" />{profile.email}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground font-mono">
                                      {feedback.user_id?.slice(0, 8) || 'unknown'}...
                                    </span>
                                  )}
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(feedback.completed_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                              <p className="text-sm text-foreground mb-3">{feedback.notes}</p>
                              <div className="flex items-center gap-4">
                                {feedback.rpe !== null && (
                                  <div className="flex items-center gap-2">
                                    <Zap className="h-4 w-4 text-orange-500" />
                                    <span className="text-xs text-muted-foreground">
                                      RPE: <span className="font-medium text-foreground">{feedback.rpe}/10</span>
                                    </span>
                                  </div>
                                )}
                                {feedback.pain_level !== null && (
                                  <div className="flex items-center gap-2">
                                    <HeartPulse className={cn("h-4 w-4",
                                      feedback.pain_level === 0 ? 'text-green-500' :
                                      feedback.pain_level <= 3 ? 'text-amber-500' :
                                      'text-red-500'
                                    )} />
                                    <span className="text-xs text-muted-foreground">
                                      Pain: <span className="font-medium text-foreground">{feedback.pain_level}/10</span>
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )})}
                        </div>
                        
                        {userEngagement.success_indicators && (
                          <div className="mt-6 pt-6 border-t">
                            <h2 className="text-sm font-medium text-muted-foreground mb-3">Success Indicators</h2>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              <div className="text-center p-3 bg-green-500/10 rounded-lg">
                                <p className="text-2xl font-bold text-green-500">
                                  {userEngagement.success_indicators.low_pain_rate?.toFixed(0)}%
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">Low Pain Rate</p>
                              </div>
                              <div className="text-center p-3 bg-blue-500/10 rounded-lg">
                                <p className="text-2xl font-bold text-blue-500">
                                  {userEngagement.success_indicators.appropriate_intensity_rate?.toFixed(0)}%
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">Optimal Intensity</p>
                              </div>
                              <div className="text-center p-3 bg-purple-500/10 rounded-lg">
                                <p className="text-2xl font-bold text-purple-500">
                                  {userEngagement.success_indicators.completion_with_notes_rate?.toFixed(0)}%
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">With Feedback</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {evidence && evidence.length > 0 && (() => {
                    const pendingItems = evidence.filter(e => e.review_status === 'pending');
                    const approvedItems = evidence.filter(e => e.review_status === 'approved' || !e.review_status);
                    const rejectedItems = evidence.filter(e => e.review_status === 'rejected');

                    const EvidenceCard = ({ item, showActions = false }) => {
                      const source = item.evidence_sources;
                      if (!source) return null;
                      const status = item.review_status || 'approved';
                      return (
                        <div className={cn(
                          "p-4 rounded-lg border",
                          status === 'pending' ? 'bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800' :
                          status === 'rejected' ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800 opacity-60' :
                          'bg-muted'
                        )}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-foreground mb-1">
                                <a href={source.url} target="_blank" rel="noopener noreferrer" className="hover:underline hover:text-primary transition-colors">
                                  {source.title}
                                </a>
                              </h4>
                              <p className="text-sm text-muted-foreground truncate mb-2">
                                {source.authors && source.authors.length > 200 ? `${source.authors.substring(0, 200)}...` : source.authors}
                              </p>
                            </div>
                            {status === 'pending' && (
                              <Badge variant="outline" className="border-amber-300 text-amber-700 dark:text-amber-400 shrink-0">
                                <CircleDot className="h-3 w-3 mr-1" />Pending
                              </Badge>
                            )}
                            {status === 'rejected' && (
                              <Badge variant="outline" className="border-red-300 text-red-600 dark:text-red-400 shrink-0">Rejected</Badge>
                            )}
                          </div>
                          <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                            <div className="flex items-center gap-4">
                              <span className="flex items-center gap-1.5"><BookText className="h-3 w-3" />{source.journal}, {source.publication_year}</span>
                              {source.study_type && <Badge variant="secondary">{source.study_type}</Badge>}
                            </div>
                            {source.relevance_score && (
                              <div className="flex items-center gap-1">
                                <Star className="h-3 w-3 text-amber-500" />
                                <span>Relevance: {source.relevance_score}/5</span>
                              </div>
                            )}
                          </div>
                          {source.abstract && (
                            <p className="text-sm text-muted-foreground italic mb-3">
                              "{source.abstract.substring(0, 300)}..."
                              {source.pubmed_id && (
                                <a href={`https://pubmed.ncbi.nlm.nih.gov/${source.pubmed_id}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline ml-2 font-semibold not-italic">
                                  View on PubMed
                                </a>
                              )}
                            </p>
                          )}
                          {showActions && canEdit && (
                            <div className="flex items-center gap-2 pt-2 border-t border-border/50">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleReviewEvidence(item.evidence_id, 'approved')}
                                disabled={reviewingEvidence[item.evidence_id]}
                                className="gap-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                              >
                                {reviewingEvidence[item.evidence_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ThumbsUp className="h-3.5 w-3.5" />}
                                Approve
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleReviewEvidence(item.evidence_id, 'rejected')}
                                disabled={reviewingEvidence[item.evidence_id]}
                                className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                              >
                                {reviewingEvidence[item.evidence_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ThumbsDown className="h-3.5 w-3.5" />}
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    };

                    return (
                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center text-lg">
                              <Microscope className="h-5 w-5 mr-3" />
                              Scientific Evidence
                            </CardTitle>
                            <div className="flex items-center gap-2">
                              {pendingItems.length > 0 && (
                                <Badge variant="outline" className="border-amber-300 text-amber-700 dark:text-amber-400">
                                  {pendingItems.length} pending review
                                </Badge>
                              )}
                              {approvedItems.length > 0 && (
                                <Badge variant="secondary">{approvedItems.length} approved</Badge>
                              )}
                            </div>
                          </div>
                          <CardDescription>
                            {evidence.length} linked article{evidence.length > 1 ? 's' : ''}.
                            {pendingItems.length > 0 && ' Pending articles are not visible to app users until approved.'}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {pendingItems.length > 0 && (
                              <div className="space-y-3">
                                <h4 className="text-sm font-medium text-amber-700 dark:text-amber-400 flex items-center gap-2">
                                  <CircleDot className="h-4 w-4" />
                                  Pending Review ({pendingItems.length})
                                </h4>
                                {pendingItems.map(item => (
                                  <EvidenceCard key={item.evidence_id} item={item} showActions />
                                ))}
                              </div>
                            )}
                            {approvedItems.length > 0 && (
                              <div className="space-y-3">
                                {pendingItems.length > 0 && (
                                  <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2 mt-2">
                                    <CheckCircle className="h-4 w-4 text-emerald-500" />
                                    Approved ({approvedItems.length})
                                  </h4>
                                )}
                                {approvedItems.map(item => (
                                  <EvidenceCard key={item.evidence_id} item={item} />
                                ))}
                              </div>
                            )}
                            {rejectedItems.length > 0 && (
                              <Accordion type="single" collapsible>
                                <AccordionItem value="rejected" className="border-none">
                                  <AccordionTrigger className="py-2 text-sm text-muted-foreground hover:no-underline">
                                    <span className="flex items-center gap-2">
                                      <ThumbsDown className="h-3.5 w-3.5" />
                                      {rejectedItems.length} rejected
                                    </span>
                                  </AccordionTrigger>
                                  <AccordionContent>
                                    <div className="space-y-3 pt-2">
                                      {rejectedItems.map(item => (
                                        <EvidenceCard key={item.evidence_id} item={item} showActions />
                                      ))}
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              </Accordion>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  {/* Where Used Section */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Where This Exercise Is Used</CardTitle>
                      <CardDescription>
                        Programs, sessions, and athlete data referencing this exercise.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Tabs defaultValue="programs">
                        <TabsList>
                          <TabsTrigger value="programs">
                            <Package className="h-3.5 w-3.5 mr-1.5" />
                            Programs
                            {(() => {
                              const programs = [...new Map(sessionProtocols.filter(p => p.sessions?.programs?.name).map(p => [p.sessions.programs.name, p.sessions.programs])).values()];
                              return programs.length > 0 ? <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">{programs.length}</Badge> : null;
                            })()}
                          </TabsTrigger>
                          <TabsTrigger value="sessions">
                            <CalendarDays className="h-3.5 w-3.5 mr-1.5" />
                            Sessions
                            {sessionProtocols.length > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">{sessionProtocols.length}</Badge>}
                          </TabsTrigger>
                          {canSeeEngagement && (
                          <TabsTrigger value="athlete-data">
                            <UserCheck className="h-3.5 w-3.5 mr-1.5" />
                            Athlete Data
                          </TabsTrigger>
                          )}
                        </TabsList>

                        <TabsContent value="programs" className="mt-4">
                          {(() => {
                            const programMap = new Map();
                            sessionProtocols.forEach(p => {
                              if (p.sessions?.programs?.name) {
                                const key = p.sessions.programs.name;
                                if (!programMap.has(key)) {
                                  programMap.set(key, { name: key, sessions: [] });
                                }
                                if (!programMap.get(key).sessions.includes(p.sessions.name)) {
                                  programMap.get(key).sessions.push(p.sessions.name);
                                }
                              }
                            });
                            const programs = [...programMap.values()];
                            if (programs.length === 0) return <p className="text-sm text-muted-foreground py-4 text-center">Not used in any programs yet.</p>;
                            return (
                              <div className="space-y-2">
                                {programs.map((prog, i) => (
                                  <div key={i} className="flex items-center justify-between p-3 rounded-md bg-muted/50">
                                    <div>
                                      <p className="text-sm font-medium">{prog.name}</p>
                                      <p className="text-xs text-muted-foreground">{prog.sessions.length} session{prog.sessions.length !== 1 ? 's' : ''} use this exercise</p>
                                    </div>
                                    <Badge variant="secondary">{prog.sessions.length}</Badge>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </TabsContent>

                        <TabsContent value="sessions" className="mt-4">
                          {sessionProtocols.length === 0 ? (
                            <p className="text-sm text-muted-foreground py-4 text-center">Not used in any sessions yet.</p>
                          ) : (
                            <div className="space-y-2">
                              {sessionProtocols.map((protocol) => (
                                <div key={protocol.id} className="flex items-center justify-between p-3 rounded-md bg-muted/50">
                                  <div>
                                    <p className="text-sm font-medium">{protocol.sessions?.name || 'Unknown session'}</p>
                                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                                      {protocol.sessions?.programs?.name && <span>Program: {protocol.sessions.programs.name}</span>}
                                      {protocol.sets && <span>{protocol.sets} sets</span>}
                                      {protocol.reps && <span>{protocol.reps} reps</span>}
                                      {protocol.exercise_group && <span>{protocol.exercise_group}</span>}
                                    </div>
                                  </div>
                                  <Badge variant="outline" className="text-xs">{protocol.exercise_group || 'main'}</Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </TabsContent>

                        <TabsContent value="athlete-data" className="mt-4">
                          {userEngagement?.completion_stats ? (
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="text-center p-3 bg-muted/50 rounded-md">
                                  <p className="text-lg font-semibold">{userEngagement.completion_stats.total_completions || 0}</p>
                                  <p className="text-[10px] text-muted-foreground">Total Completions</p>
                                </div>
                                <div className="text-center p-3 bg-muted/50 rounded-md">
                                  <p className="text-lg font-semibold">{userEngagement.completion_stats.unique_users || 0}</p>
                                  <p className="text-[10px] text-muted-foreground">Unique Athletes</p>
                                </div>
                                <div className="text-center p-3 bg-muted/50 rounded-md">
                                  <p className="text-lg font-semibold">{userEngagement.completion_stats.avg_rpe?.toFixed(1) || '-'}</p>
                                  <p className="text-[10px] text-muted-foreground">Avg RPE</p>
                                </div>
                                <div className="text-center p-3 bg-muted/50 rounded-md">
                                  <p className="text-lg font-semibold">{userEngagement.completion_stats.avg_pain_level?.toFixed(1) || '-'}</p>
                                  <p className="text-[10px] text-muted-foreground">Avg Pain</p>
                                </div>
                              </div>
                              {userEngagement.recent_feedback?.length > 0 && (
                                <div>
                                  <h4 className="text-xs font-medium text-muted-foreground mb-2">Recent Athlete Notes</h4>
                                  <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {userEngagement.recent_feedback.slice(0, 8).map((fb, i) => {
                                      const profile = userProfiles[fb.user_id];
                                      return (
                                      <div key={i} className="p-2.5 rounded-md bg-muted/50 text-xs">
                                        <div className="flex items-center justify-between mb-1">
                                          {profile ? (
                                            <div className="flex items-center gap-2">
                                              <span className="font-medium text-foreground">{profile.full_name}</span>
                                              {profile.email && <span className="text-muted-foreground">{profile.email}</span>}
                                            </div>
                                          ) : (
                                            <span className="font-mono text-muted-foreground">{fb.user_id?.slice(0, 8)}...</span>
                                          )}
                                          <div className="flex items-center gap-2 text-muted-foreground">
                                            {fb.rpe != null && <span>RPE {fb.rpe}</span>}
                                            {fb.pain_level != null && <span className={fb.pain_level > 3 ? 'text-red-500' : ''}>Pain {fb.pain_level}</span>}
                                          </div>
                                        </div>
                                        {fb.notes && <p className="text-foreground">{fb.notes}</p>}
                                      </div>
                                    )})}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground py-4 text-center">No athlete data available yet.</p>
                          )}
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>

                  {sessionProtocols.length > 0 && (
                      <div>
                          <h2 className="text-sm font-medium text-muted-foreground mb-3">Session Protocols (Detailed)</h2>
                          <div className="space-y-6">
                              {sessionProtocols.map(protocol => (
                                  <SessionProtocolCard key={protocol.id} protocol={protocol} />
                              ))}
                          </div>
                      </div>
                  )}
              </div>

              <div className="space-y-8">
                  <Card>
                      <CardHeader><CardTitle className="text-base">Key Details</CardTitle></CardHeader>
                      <CardContent className="space-y-4">
                          <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground flex items-center"><BarChart3 className="mr-2 h-4 w-4" />Difficulty</span> <Badge variant="secondary">{exercise.difficulty}</Badge></div>
                          <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground flex items-center"><Target className="mr-2 h-4 w-4" />Primary Region</span> <Badge variant="secondary">{exercise.primary_region}</Badge></div>
                          <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground flex items-center"><HeartPulse className="mr-2 h-4 w-4" />Rehab Stage</span> <Badge variant="secondary">{exercise.stage_of_rehab}</Badge></div>
                          <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground flex items-center"><Shield className="mr-2 h-4 w-4" />In Coach Library</span> <Badge variant={exercise.is_admin_library ? 'success' : 'destructive'}>{exercise.is_admin_library ? 'Yes' : 'No'}</Badge></div>
                      </CardContent>
                  </Card>

                  {exercise?.equipment && exercise.equipment.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center text-base">
                          <Dumbbell className="h-4 w-4 mr-2 text-muted-foreground" />
                          Equipment
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {exercise.equipment.map((eq, index) => (
                          <div key={index} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                              {eq.is_required ? (
                                <CheckCircle className="h-4 w-4 text-green-500" />
                              ) : (
                                <Circle className="h-4 w-4 text-muted-foreground" />
                              )}
                              <span className="font-medium text-foreground">
                                {eq.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={eq.is_required ? 'destructive' : 'outline'}>
                                  {eq.is_required ? 'Required' : 'Optional'}
                              </Badge>
                              {eq.quantity > 1 && (
                                <Badge variant="secondary" className="rounded-full">
                                    x{eq.quantity}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}

                  {exercise?.phases && exercise.phases.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center text-base">
                          <Shuffle className="h-4 w-4 mr-2 text-muted-foreground" />
                          Phase Usage
                        </CardTitle>
                        <CardDescription>
                          Used in {exercise.phases.length} different workout phases
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {exercise.phases.map((phase, index) => (
                            <Badge key={index} variant="secondary">
                              {phase.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </Badge>
                          ))}
                        </div>
                        {exercise?.phase_distribution && (
                          <div className="space-y-2 text-sm">
                            <h4 className="font-medium text-foreground">Usage Breakdown:</h4>
                            {Object.entries(exercise.phase_distribution).map(([phase, count]) => (
                              <div key={phase} className="flex justify-between items-center text-muted-foreground">
                                <span>
                                  - {phase.replace(/_/g, ' ')}
                                </span>
                                <span className="font-bold">{count}x</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center text-base">
                        <TrendingUp className="h-4 w-4 mr-2 text-muted-foreground" />
                        Progressions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{exercise.progressions || 'No progressions noted.'}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center text-base">
                        <TrendingDown className="h-4 w-4 mr-2 text-muted-foreground" />
                        Regressions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{exercise.regression_notes || 'No regression notes available.'}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center text-base">
                        <Code className="h-4 w-4 mr-2 text-muted-foreground" />
                        Medical Info
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-1">Conditions</h4>
                        <p className="text-sm text-muted-foreground">{exercise.conditions || 'None specified.'}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground mb-1">Contraindications</h4>
                        <p className="text-sm text-muted-foreground">{exercise.contraindications || 'None specified.'}</p>
                      </div>
                    </CardContent>
                  </Card>
              </div>
          </div>

        </main>
      </div>

      <BookmarkForAthleteDialog
        exerciseId={id}
        exerciseName={exercise?.name}
        open={bookmarkForAthleteOpen}
        onOpenChange={setBookmarkForAthleteOpen}
      />
    </>
  );
}

export default ExerciseDetail;
